import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Conference } from '../../api/types';
import { archiveConference } from '../../api/archive';
import { CONFERENCE_ARCHIVE_ENABLED } from '../../config/features';
import {
  conferenceCopy,
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc, and
 * the console counterpart of this file.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * Portal counterpart to the console's active-conferences list
 * (apps/console/src/resources/conferences/list.tsx) — same DataTable column
 * template, so the two apps present and behave identically.
 *
 * `manage:Conference` gates the per-row Archive action. Opening a conference
 * is the ROW CLICK — there is deliberately no Edit or View button (see the
 * DataTable spec). The whole /conferences route tree is additionally gated
 * to company_admin via RoleGuard (apps/portal/src/App.tsx), so the
 * component-level cap check here is defense in depth, not a new restriction.
 */
export function ConferenceList() {
  const { tableProps } = useTable<Conference>({
    resource: 'conferences',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  // Archive row-action, hidden until the backend supports admin-plane
  // conference archive (see CONFERENCE_ARCHIVE_ENABLED).
  const archiveActionsColumn: DataColumn<Conference> = {
    key: 'actions',
    title: '',
    kind: 'actions',
    render: (_: unknown, record: Conference) => (
      <RowActionsMenu
        actions={[
          {
            key: 'archive',
            label: 'Archive',
            danger: true,
            cap: 'manage:Conference',
            onRun: async () => {
              await archiveConference(record.id);
              invalidate({ resource: 'conferences', invalidates: ['list'] });
            },
            confirm: {
              title: `Archive ${record.name}?`,
              content: conferenceCopy.archiveBodyWithRestoreHint,
              okText: 'Archive',
              danger: true,
              successMessage: `Archived ${record.name}.`,
            },
          },
        ]}
      />
    ),
  };

  const columns: DataColumn<Conference>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 260 },
    { dataIndex: 'location', key: 'location', title: 'Location', kind: 'text', width: 200 },
    { dataIndex: 'startDate', key: 'startDate', title: 'Starts', kind: 'date' },
    { dataIndex: 'endDate', key: 'endDate', title: 'Ends', kind: 'date' },
    ...(CONFERENCE_ARCHIVE_ENABLED ? [archiveActionsColumn] : []),
  ];

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          {CONFERENCE_ARCHIVE_ENABLED && (
            <Link to="/conferences/archived">View archived →</Link>
          )}
        </>
      )}
    >
      <DataTable<Conference>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="conferencesList"
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'location']}
        searchPlaceholder="Search conferences"
        onRowClick={(record) => navigate(`/conferences/${record.id}`)}
        empty={{
          title: 'No conferences yet',
          description: 'Create the first conference to get started.',
        }}
      />
    </List>
  );
}
